import React, { lazy, useEffect, useId, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { ObservationImage } from './BuildingDemo';
import { DemoId, DemoLocale, DemoYear, demoIds, demoYears } from './demoData';
import { Action, colors, fonts } from './design';
import { navigatePublic, publicPath } from './routes';
import {
  buildingTeaserObservations, buildingTeaserSearch, buildingTeaserViews, parseBuildingTeaser,
  parseBuildingTeaserView, teaserDisclaimer, teaserPeriodLabel, teaserPhotoLabel, teaserScenarioNote,
} from './buildingTeaserData';
import type { BuildingTeaserView } from './buildingTeaserData';
import { TeaserImage } from './TeaserImage';
import { TeaserRecommendations } from './TeaserRecommendations';
import AsyncView from '../../common/AsyncView';

const BuildingTeaserModel = lazy(() => import('./BuildingTeaserModel'));

function YearTabs({ year, onChange, locale, id }: { year: DemoYear; onChange: (year: DemoYear) => void; locale: DemoLocale; id: string }) {
  return <Tabs value={year} onChange={(_, value: DemoYear) => onChange(value)} selectionFollowsFocus
    variant="fullWidth" aria-label={locale === 'sv' ? 'År i exemplet' : 'Example year'} sx={{ maxWidth: 440 }}>
    {demoYears.map(value => <Tab key={value} value={value} label={value}
      id={`${id}-${value}`} aria-controls={`${id}-panel`} sx={{ minWidth: 0, minHeight: 48 }} />)}
  </Tabs>;
}

export function BuildingTeaser({ search = '', locale = 'sv' }: { search?: string; locale?: DemoLocale }) {
  const parsed = parseBuildingTeaser(search);
  const parsedView = parseBuildingTeaserView(search);
  const [selectedId, setSelectedId] = useState<DemoId>(parsed.selected);
  const [year, setYear] = useState<DemoYear>(parsed.year);
  const [view, setView] = useState<BuildingTeaserView>(parsedView);
  const [fullscreen, setFullscreen] = useState(false);
  const selectedButton = useRef<HTMLButtonElement>(null);
  const id = useId();
  const en = locale === 'en';
  const items = buildingTeaserObservations(locale, year);
  const selected = items.find(item => item.id === selectedId)!;
  useEffect(() => setSelectedId(parsed.selected), [parsed.selected]);
  useEffect(() => setYear(parsed.year), [parsed.year]);
  useEffect(() => setView(parsedView), [parsedView]);

  const select = (next: DemoId, nextView: BuildingTeaserView = view, nextYear: DemoYear = year) => {
    setSelectedId(next);
    setView(nextView);
    setYear(nextYear);
    const destination = `${publicPath('buildingPass', locale)}${buildingTeaserSearch(next, search, nextYear, nextView)}${window.location.hash}`;
    if (destination !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      navigatePublic(destination, { replace: true, preserveScroll: true });
    }
  };
  const cycle = (direction: number) => select(demoIds[(demoIds.indexOf(selectedId) + direction + demoIds.length) % demoIds.length]);

  return <Box sx={{ bgcolor: colors.white, border: `1px solid ${colors.line}`, minWidth: 0 }}>
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <Box sx={{ mr: 'auto', minWidth: 0 }}>
        <Typography sx={{ fontFamily: fonts.mono, color: colors.muted, fontSize: 11, mb: 1 }}>
          {en ? 'THE BUILDING PASS / A FIRST LOOK' : 'BYGGNADSPASSET / EN FÖRSTA TITT'}
        </Typography>
        <Typography component="h2" sx={{ fontSize: { xs: 25, md: 32 }, fontWeight: 700 }}>
          {en ? 'From building to detail.' : 'Från byggnad till detalj.'}
        </Typography>
      </Box>
      <Action href={`${publicPath('exampleReport', locale)}${buildingTeaserSearch(selectedId, '', year, view)}`} secondary>
        {en ? 'Sample report' : 'Exempelrapport'}
      </Action>
    </Box>
    {parsed.invalid && <Alert severity="warning">{en
      ? 'Invalid image, model view or example year in the link. Valid selections are retained; invalid values use the first finding, Standard or 2026.'
      : 'Ogiltigt bildval, modellvy eller exempelår i länken. Giltiga val behålls; ogiltiga värden ersätts med första fyndet, Standard eller 2026.'}</Alert>}
    <Box sx={{ px: { xs: 2, md: 3 }, pb: 2 }}>
      <Typography sx={{ fontSize: 13, color: colors.muted, mb: 1 }}>{teaserScenarioNote[locale]}</Typography>
      <YearTabs year={year} onChange={next => select(selectedId, view, next)} locale={locale} id={`${id}-year`} />
    </Box>
    <Box role="tabpanel" id={`${id}-year-panel`} aria-labelledby={`${id}-year-${year}`}
      sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.2fr) minmax(0, 1fr)' }, borderTop: `1px solid ${colors.line}` }}>
      <Box sx={{ minWidth: 0, bgcolor: colors.paper, p: { xs: 1.5, md: 2.5 } }}>
        <Box role="group" aria-label={en ? 'Model view' : 'Modellvy'}
          sx={{ display: 'flex', gap: .5, p: .5, mb: 1.5, border: `1px solid ${colors.line}`, bgcolor: colors.white }}>
          {buildingTeaserViews.map(mode => <Button key={mode} onClick={() => select(selectedId, mode)}
            aria-pressed={view === mode} aria-controls={`${id}-model`}
            sx={{ flex: 1, minWidth: 0, minHeight: 44, px: 1, fontSize: { xs: 12, sm: 13 },
              color: view === mode ? colors.white : colors.ink, bgcolor: view === mode ? colors.ink : 'transparent',
              '&:hover': { bgcolor: view === mode ? colors.graphite : colors.paper } }}>
            {mode === 'existing' ? 'Standard'
              : mode === '3d' ? (en ? 'Detailed 3D' : 'Detaljerad 3D') : (en ? '2D facades' : '2D-fasader')}
          </Button>)}
        </Box>
        <Box id={`${id}-model`}>
          {view === 'existing' ? <ObservationImage items={items} selected={selectedId} onSelect={select} locale={locale} compactMarkers
            locationLabel={en ? 'CORNER BUILDING' : 'HÖRNHUSET'}
            background={<TeaserImage image="building" locale={locale}
              alt={en ? 'Illustrative model of the corner building, with red roof and two street façades' : 'Illustrativ modell av hörnhuset, med rött tak och två gatufasader'} />} />
            : <AsyncView key={view} name={en ? 'Model view' : 'Modellvyn'} locale={locale}>
              <BuildingTeaserModel view={view} selectedId={selectedId} onSelect={select} locale={locale} year={year} />
            </AsyncView>}
        </Box>
        <Typography sx={{ color: colors.muted, fontSize: 13, mt: 2, mb: 1.5 }}>
          {en ? 'Select a marker or an observation to see the linked photo.' : 'Välj en markör eller en observation för att se den kopplade bilden.'}
        </Typography>
        <Box role="group" aria-label={en ? 'Observations' : 'Observationer'} sx={{ display: 'grid', gap: 1 }}>
          {items.map(item => <Button key={item.id} onClick={() => select(item.id)} aria-pressed={item.id === selectedId}
            ref={item.id === selectedId ? selectedButton : undefined}
            sx={{ justifyContent: 'flex-start', textAlign: 'left', minWidth: 0, minHeight: 44, gap: 2,
              bgcolor: item.id === selectedId ? colors.white : 'transparent', color: colors.ink,
              border: `1px solid ${item.id === selectedId ? colors.ink : colors.line}` }}>
            <Box component="span" sx={{ fontFamily: fonts.mono, fontSize: 12 }}>{item.id.slice(-2)}</Box>{item.title}
          </Button>)}
        </Box>
        <Button startIcon={<RestartAltIcon />} onClick={() => { select('F-001', 'existing', 2026); setFullscreen(false); }} sx={{ mt: 1.5 }}>
          {en ? 'Reset the view' : 'Återställ vyn'}
        </Button>
      </Box>
      <Box id="observation" sx={{ minWidth: 0, p: { xs: 2, md: 3 }, scrollMarginTop: 110 }}>
        <Typography sx={{ fontFamily: fonts.mono, fontSize: 11, color: colors.muted, mb: 1 }}>
          {selected.id} / {selected.location}
        </Typography>
        <Typography component="h3" sx={{ fontWeight: 700, fontSize: 25, mb: 1 }}>{selected.title}</Typography>
        <Chip size="small" label={selected.status} sx={{ bgcolor: colors.soft, mb: 2 }} />
        <TeaserImage image={selected.photo} alt={selected.photoAlt} locale={locale} finding={selected} />
        <Typography sx={{ fontSize: 12, color: colors.muted, mt: 1, overflowWrap: 'anywhere' }}>
          {teaserPhotoLabel(selected, locale)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <Typography component={year === 2026 ? 'time' : 'p'} dateTime={year === 2026 ? '2026-06-13' : undefined} sx={{ color: colors.muted, fontSize: 12 }}>
            {teaserPeriodLabel(selected, locale)}
          </Typography>
          <Button size="small" startIcon={<OpenInFullIcon />} disabled={selected.media.kind === 'unavailable'} onClick={() => setFullscreen(true)}>
            {en ? 'Enlarge close-up' : 'Förstora närbilden'}
          </Button>
        </Box>
        {selected.media.kind !== 'unavailable' && <Box component="section" aria-labelledby={`${id}-observation`} sx={{ mt: 2.5 }}>
          <Typography component="h4" id={`${id}-observation`} sx={{ fontWeight: 700, mb: 1 }}>Observation</Typography>
          <Typography sx={{ color: colors.muted, fontSize: 15 }}>{selected.assessment}</Typography>
        </Box>}
        {selected.media.kind !== 'unavailable' && <TeaserRecommendations finding={selected} locale={locale} />}
      </Box>
    </Box>
    <Typography sx={{ px: { xs: 2, md: 3 }, py: 2, fontSize: 12, color: colors.muted, borderTop: `1px solid ${colors.line}` }}>
      {year === 2026 ? teaserDisclaimer[locale] : en
        ? 'Select a marker to follow the same detail through the example years.'
        : 'Välj en markör för att följa samma detalj genom exempelåren.'}
    </Typography>
    <Dialog open={fullscreen} onClose={() => setFullscreen(false)} fullWidth maxWidth="lg" aria-labelledby={`${id}-fullscreen`}
      TransitionProps={{ onExited: () => { if (selected.media.kind === 'unavailable') selectedButton.current?.focus(); } }}
      onKeyDown={event => {
        if (event.target instanceof HTMLElement && event.target.closest('[role="tablist"]')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        cycle(event.key === 'ArrowRight' ? 1 : -1);
      }}>
      <DialogTitle id={`${id}-fullscreen`} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {selected.id} · {selected.title}
        <IconButton aria-label={en ? 'Close close-up' : 'Stäng närbilden'} onClick={() => setFullscreen(false)} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: colors.muted, mb: 1 }}>{teaserScenarioNote[locale]}</Typography>
        <YearTabs year={year} onChange={next => select(selectedId, view, next)} locale={locale} id={`${id}-zoom-year`} />
        <Box role="tabpanel" id={`${id}-zoom-year-panel`} aria-labelledby={`${id}-zoom-year-${year}`}>
          <TeaserImage image={selected.photo} alt={selected.photoAlt} locale={locale} finding={selected} full eager />
        </Box>
        <Typography sx={{ mt: 1.5, fontSize: 13 }}>{selected.location} · {teaserPeriodLabel(selected, locale)}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <Button onClick={() => cycle(-1)}>{en ? 'Previous photo' : 'Föregående bild'}</Button>
          <Button onClick={() => cycle(1)}>{en ? 'Next photo' : 'Nästa bild'}</Button>
        </Box>
      </DialogContent>
    </Dialog>
  </Box>;
}
